import expensesService from '@/services/expenses';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractPayload,
  resolveApiPayload,
} from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { buildExpenseStoreRequestData } from '@/composables/expenses/useExpenseModalForm';
import { EXPENSES_TEXT } from '@/texts/catalog/expenses';

const FLOW_TEXT = EXPENSES_TEXT.flow;
const CONFIRM_TEXT = EXPENSES_TEXT?.page?.confirm || {};

const normalizeMode = (mode = 'create') => {
  return String(mode || '').trim().toLowerCase() === 'edit'
    ? 'edit'
    : 'create';
};

export const openExpenseModalState = (vm, mode = 'create', initialData = null) => {
  vm.expenseModalMode = normalizeMode(mode);
  vm.expenseModalInitialData = initialData && typeof initialData === 'object'
    ? { ...initialData }
    : null;
  vm.isExpenseModalOpen = true;
};

export const closeExpenseModalState = (vm) => {
  if (vm.isExpenseSubmitting) {
    return;
  }

  vm.isExpenseModalOpen = false;
  vm.expenseModalMode = 'create';
  vm.expenseModalInitialData = null;
};

export const submitExpenseFlow = async (
  vm,
  payload,
  {
    saveErrorMessage = FLOW_TEXT.errors.save,
    createSuccessMessage = FLOW_TEXT.success.create,
    updateSuccessMessage = FLOW_TEXT.success.update,
    mapRequestData = null,
    onSuccess = null,
  } = {}
) => {
  vm.isExpenseSubmitting = true;

  try {
    const requestData = buildExpenseStoreRequestData(payload);
    if (typeof mapRequestData === 'function') {
      mapRequestData(requestData, { vm, payload });
    }

    const isEditMode = normalizeMode(vm.expenseModalMode) === 'edit';
    const expenseId = Number(vm.expenseModalInitialData?.id || 0);
    const response = isEditMode && expenseId > 0
      ? await expensesService.update(expenseId, requestData)
      : await expensesService.store(requestData);

    const apiErrorMessage = extractApiErrorMessage(response, saveErrorMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    const responsePayload = resolveApiPayload(response);
    const responseType = String(responsePayload?.type || '').trim().toLowerCase();
    if (responseType !== '' && responseType !== 'success') {
      vm.$toast?.error(saveErrorMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(
      response,
      isEditMode ? updateSuccessMessage : createSuccessMessage,
    ));

    vm.isExpenseModalOpen = false;
    vm.expenseModalMode = 'create';
    vm.expenseModalInitialData = null;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, payload, requestData, response, isEditMode });
    }
  } catch (error) {
    const errorPayload = error?.response?.data || error?.data || error;
    const apiErrorMessage = extractApiErrorMessage(errorPayload, '');
    const message = apiErrorMessage
      || String(errorPayload?.message || '').trim()
      || String(error?.message || '').trim()
      || saveErrorMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isExpenseSubmitting = false;
  }
};

export const openExpenseEditModalFromRow = async (
  vm,
  row,
  {
    detailErrorMessage = FLOW_TEXT.errors.detail,
    mapInitialData = null,
  } = {}
) => {
  const expenseId = Number(row?.id || 0);
  if (!expenseId) {
    return;
  }

  try {
    const response = await expensesService.show(expenseId, {}, { skipLoading: true });
    const payload = extractPayload(response);
    const detail = payload && typeof payload === 'object' ? payload : {};
    const rowData = row && typeof row === 'object' ? row : {};

    const initialData = typeof mapInitialData === 'function'
      ? mapInitialData({ detail, rowData, payload })
      : (detail && Object.keys(detail).length > 0 ? detail : rowData);

    openExpenseModalState(vm, 'edit', initialData);
  } catch (error) {
    vm.$toast?.error(detailErrorMessage);
  }
};

export const notifyExpenseActionPending = (
  vm,
  row,
  {
    actionLabel = '',
    referencePrefix = FLOW_TEXT.pending.referencePrefix,
    suffix = FLOW_TEXT.pending.suffix,
  } = {}
) => {
  const reference = String(row?.reference || '').trim() || `${referencePrefix} #${row?.id || '-'}`;
  const action = String(actionLabel || '').trim();
  const message = action === ''
    ? `${reference} ${suffix}`
    : `${action} ${reference} ${suffix}`;

  vm.$toast?.info(message.trim());
};

export const resolveExpenseApprovalTargetState = (row, explicitState = null) => {
  if (typeof explicitState === 'boolean') {
    return explicitState;
  }

  const approved = row?.is_approved;
  if (approved === true) {
    return false;
  }

  if (approved === false) {
    return true;
  }

  if (approved === undefined || approved === null || approved === '') {
    return true;
  }

  return Number(approved) !== 1;
};

export const resolveExpenseApproveModalContent = ({
  row = null,
  approveTargetState = true,
  isApproving = false,
  confirmText = CONFIRM_TEXT,
} = {}) => {
  const source = confirmText && typeof confirmText === 'object' ? confirmText : {};
  const approveCopy = source?.approve && typeof source.approve === 'object' ? source.approve : {};

  let message = approveTargetState
    ? String(approveCopy?.messageApproveDefault || '').trim()
    : String(approveCopy?.messageUnapproveDefault || '').trim();

  if (row && typeof row === 'object') {
    const provider = String(row?.provider || '').trim() || String(source?.noProvider || '').trim();
    const concept = String(row?.concept || '').trim() || String(source?.noConcept || '').trim();
    const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
      currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
    });

    const summaryResolver = source?.summary;
    message = typeof summaryResolver === 'function'
      ? String(summaryResolver({ provider, concept, amount }) || '').trim()
      : '';
  }

  const confirmActionText = isApproving
    ? (approveTargetState
      ? String(approveCopy?.confirmingApprove || '').trim()
      : String(approveCopy?.confirmingUpdate || '').trim())
    : (approveTargetState
      ? String(approveCopy?.confirmApprove || '').trim()
      : String(approveCopy?.confirmUnapprove || '').trim());

  return {
    title: approveTargetState
      ? String(approveCopy?.titleApprove || '').trim()
      : String(approveCopy?.titleUnapprove || '').trim(),
    subtitle: approveTargetState
      ? String(approveCopy?.subtitleApprove || '').trim()
      : String(approveCopy?.subtitleUnapprove || '').trim(),
    message,
    confirmText: confirmActionText,
    cancelText: String(source?.cancel || '').trim(),
  };
};

export const resolveExpenseDeleteModalContent = ({
  row = null,
  isDeleting = false,
  confirmText = CONFIRM_TEXT,
} = {}) => {
  const source = confirmText && typeof confirmText === 'object' ? confirmText : {};
  const deleteCopy = source?.delete && typeof source.delete === 'object' ? source.delete : {};

  let message = String(deleteCopy?.messageDefault || '').trim();

  if (row && typeof row === 'object') {
    const provider = String(row?.provider || '').trim() || String(source?.noProvider || '').trim();
    const concept = String(row?.concept || '').trim() || String(source?.noConcept || '').trim();
    const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
      currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
    });

    const summaryResolver = source?.summary;
    if (typeof summaryResolver === 'function') {
      message = String(summaryResolver({ provider, concept, amount }) || '').trim();
    }
  }

  return {
    title: String(deleteCopy?.title || '').trim(),
    subtitle: String(deleteCopy?.subtitle || '').trim(),
    message,
    confirmText: isDeleting
      ? String(deleteCopy?.confirming || '').trim()
      : String(deleteCopy?.confirm || '').trim(),
    cancelText: String(source?.cancel || '').trim(),
  };
};

export const submitExpensePayFlow = async (
  vm,
  payload = {},
  {
    errorIdentifyingMessage = FLOW_TEXT.errors.payIdentify,
    errorSaveMessage = FLOW_TEXT.errors.paySave,
    successMessage = FLOW_TEXT.success.pay,
    onSuccess = null,
  } = {}
) => {
  if (vm.isPayExpenseSubmitting) {
    return;
  }

  const expenseId = Number(
    vm.payExpenseTarget?.id
    || payload?.expense_id
    || 0
  ) || 0;

  if (expenseId <= 0) {
    vm.$toast?.error(errorIdentifyingMessage);
    return;
  }

  vm.isPayExpenseSubmitting = true;

  try {
    const response = await expensesService.pay(expenseId, payload || {});
    const apiErrorMessage = extractApiErrorMessage(response, errorSaveMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    const responsePayload = resolveApiPayload(response);
    const responseType = String(responsePayload?.type || '').trim().toLowerCase();
    if (responseType !== '' && responseType !== 'success') {
      vm.$toast?.error(errorSaveMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(response, successMessage));
    vm.isPayExpenseModalOpen = false;
    vm.payExpenseTarget = null;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, payload, response });
    }
  } catch (error) {
    const errorPayload = error?.response?.data || error?.data || error;
    const apiErrorMessage = extractApiErrorMessage(errorPayload, '');
    const message = apiErrorMessage
      || String(errorPayload?.message || '').trim()
      || String(error?.message || '').trim()
      || errorSaveMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isPayExpenseSubmitting = false;
  }
};

export const confirmExpenseDeleteFlow = async (
  vm,
  {
    errorMessage = FLOW_TEXT.errors.delete,
    successMessage = FLOW_TEXT.success.delete,
    onSuccess = null,
  } = {}
) => {
  if (vm.isDeletingExpense) {
    return;
  }

  const expenseId = Number(vm.deleteExpenseTarget?.id || 0);
  if (expenseId <= 0) {
    vm.isDeleteExpenseModalOpen = false;
    vm.deleteExpenseTarget = null;
    return;
  }

  vm.isDeletingExpense = true;

  try {
    const response = await expensesService.delete(expenseId);
    const apiErrorMessage = extractApiErrorMessage(response, errorMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(response, successMessage));
    vm.isDeleteExpenseModalOpen = false;
    vm.deleteExpenseTarget = null;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, response, expenseId });
    }
  } catch (error) {
    const message = String(error?.response?.data?.message || '').trim() || errorMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isDeletingExpense = false;
  }
};

export const confirmExpenseApproveFlow = async (
  vm,
  {
    errorMessage = FLOW_TEXT.errors.approve,
    successApprovedMessage = FLOW_TEXT.success.approve,
    successUnapprovedMessage = FLOW_TEXT.success.unapprove,
    onSuccess = null,
  } = {}
) => {
  if (vm.isApprovingExpense) {
    return;
  }

  const expenseId = Number(vm.approveExpenseTarget?.id || 0);
  if (expenseId <= 0) {
    vm.isApproveExpenseModalOpen = false;
    vm.approveExpenseTarget = null;
    vm.approveExpenseTargetState = true;
    return;
  }

  vm.isApprovingExpense = true;

  try {
    const response = await expensesService.approve(expenseId, {
      approved: vm.approveExpenseTargetState,
    });

    const apiErrorMessage = extractApiErrorMessage(response, errorMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(
      response,
      vm.approveExpenseTargetState ? successApprovedMessage : successUnapprovedMessage,
    ));
    vm.isApproveExpenseModalOpen = false;
    vm.approveExpenseTarget = null;
    vm.approveExpenseTargetState = true;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, response, expenseId });
    }
  } catch (error) {
    const message = String(error?.response?.data?.message || '').trim() || errorMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isApprovingExpense = false;
  }
};
